/**
 * 読み込み中・失敗・空っぽ の見せ方をそろえる小さな部品。
 *
 * 見た目は共通部品（docs/06 4-6）に寄せてある。失敗のときは理由だけで終わらせず、
 * **次に何をすればよいか**まで出す（docs/06 原則2）。
 *
 * 次にしていただくことは、**失敗の種類で変える**（総点検 J-08）。
 * 権限が無いとき・見つからないときに「少し時間をおいて、もう一度お試しください」と出すと、
 * 待っても直らないのに待たせてしまうため、その2つでは「もう一度読み込む」も出さない。
 */
import type { ReactNode } from 'react';
import { messageForStatus } from '../api/client.js';
import { mentionsNextStep, nextStepFor, type LoadFailure } from '../api/load-failure.js';
import { Button } from './ui/Button.js';
import { ResultBanner } from './ui/ResultBanner.js';

export function LoadingBlock({ label = '読み込んでいます…' }: { label?: string }): ReactNode {
  return (
    <p role="status" aria-live="polite">
      {label}
    </p>
  );
}

export function ErrorBlock({
  failure,
  message,
  nextStep,
  onRetry,
}: {
  /** 読み込めなかった理由（種類つき）。種類で次の一手を変える */
  failure?: LoadFailure;
  /** 種類の分からない失敗の言葉（failure が無いとき） */
  message?: string;
  nextStep?: string;
  onRetry?: () => void;
}): ReactNode {
  const guide = nextStepFor(failure?.status ?? 0, failure?.retryable);
  const reason = failure?.message ?? message ?? messageForStatus(0);
  // 理由の文にもう次の一手が書いてあるとき（サーバの「少し時間をおいて…」など）は重ねない（#334 L-1）
  const step = nextStep ?? (mentionsNextStep(reason) ? undefined : guide.nextStep);
  return (
    <ResultBanner
      tone="error"
      message={reason}
      {...(step !== undefined ? { nextStep: step } : {})}
      action={
        onRetry && guide.retryable ? (
          <Button variant="secondary" compact onClick={onRetry}>
            もう一度読み込む
          </Button>
        ) : undefined
      }
    />
  );
}

export function EmptyBlock({ label }: { label: string }): ReactNode {
  return <p className="admin-empty">{label}</p>;
}
